import {
  Text,
  Flex,
  Box,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from '@everlywell/ui-kit';
import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import renderRichText from 'utils/contentful/renderRichText';

import { WeightLossSliderProps } from '../InteractiveBanner';

const SLIDER_MIN_VALUE = 150;
const SLIDER_MAX_VALUE = 400;

const WeightLossSlider: React.FC<WeightLossSliderProps | null> = (props) => {
  const [sliderValue, setSliderValue] = useState(SLIDER_MIN_VALUE);
  const [hasAnimated, setHasAnimated] = useState(false);

  const [sliderValueRef, isSliderValueInView] = useInView();

  useEffect(() => {
    if (!isSliderValueInView || hasAnimated) {
      return;
    }

    let currentSliderValue = SLIDER_MIN_VALUE;
    // Animate slider value from 100 to 200 in 5 increments. This takes 1 second.
    const intervalId = setInterval(() => {
      if (currentSliderValue < 200) {
        currentSliderValue += 5;
        setSliderValue(currentSliderValue);
      } else {
        clearInterval(intervalId);
        setHasAnimated(true);
      }
    }, 1000 / ((200 - SLIDER_MIN_VALUE) / 5));

    return () => clearInterval(intervalId);
  }, [isSliderValueInView, hasAnimated]);

  if (!props || !props.weightLossSlider) {
    return null;
  }

  const { weightLossSlider } = props;

  return (
    <Box flex={1}>
      {weightLossSlider?.map((item, index) => (
        <Box
          padding={6}
          borderRadius={'lg'}
          key={index}
          backgroundColor={item.metadata.theme.backgroundColor}
          backgroundImage={
            item.desktopImage?.gatsbyImageData?.images?.fallback?.src ||
            undefined
          }
          backgroundRepeat="no-repeat"
          backgroundPosition={['center 150%', 'center bottom']}
          minHeight={['320px', '400px']}
          display="flex"
          flexFlow={'column'}
          justifyContent={'space-between'}
        >
          <Flex justifyContent="space-between" alignItems={'center'}>
            <Text color={item.metadata.theme.textColor} fontSize="2xl">
              {item.eyebrow}
            </Text>
            <Text
              color={item.metadata.theme.textColor}
              fontSize={['2xl', '3xl']}
            >
              <Text
                as="span"
                fontSize={['6xl', '8xl']}
                fontWeight="normal"
                fontFamily="heading"
              >
                {sliderValue}
              </Text>
              lbs
            </Text>
          </Flex>
          <Slider
            aria-label="weight-loss-slider"
            defaultValue={SLIDER_MIN_VALUE}
            height="80px"
            top={['-25px', '-40px']}
            width={'94%'}
            marginX={'auto'}
            min={SLIDER_MIN_VALUE}
            max={SLIDER_MAX_VALUE}
            value={sliderValue}
            onChange={(val) => setSliderValue(val)}
          >
            <SliderTrack background={item.metadata.theme.textColor}>
              <SliderFilledTrack background={item.metadata.theme.textColor} />
            </SliderTrack>
            <SliderThumb
              height="40px"
              width="40px"
              background={'eggplant.lighter'}
              border="4px"
              borderColor={item.metadata.theme.textColor}
              borderRadius={'full'}
            />
          </Slider>
          <Flex
            justifyContent="space-between"
            alignItems={['center', 'baseline']}
            ref={sliderValueRef}
            aria-live="polite"
            role="status"
          >
            <Text
              color={item.metadata.theme.primaryColor}
              fontSize={['2xl', '3xl']}
              fontWeight="medium"
              maxWidth={['115px', '100%']}
            >
              {item?.body && item?.body?.raw ? renderRichText(item.body) : null}
            </Text>
            <Text color={item.metadata.theme.primaryColor} fontSize="3xl">
              <Text as="span" fontSize={[80, '112px']} fontFamily="heading">
                {(
                  sliderValue *
                  (item?.metadata?.context?.calculation?.percentage / 100)
                ).toFixed(0)}
              </Text>
              lbs
            </Text>
          </Flex>
        </Box>
      ))}
    </Box>
  );
};

export default WeightLossSlider;
